import {Helmet} from '@dr.pogodin/react-helmet';
import {captureMessage} from '@sentry/browser';
import {isFeatureEnabled} from 'feature-flags';
import {
  Action,
  Parameter,
  openDeleteDialog,
  openDialog,
  showNotification,
} from 'platform/components';
import {useDateTimeFormatter} from 'platform/locale';
import {match} from 'ts-pattern';

import {useState} from 'react';

import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {
  useGetDocumentContextCountQuery,
  useGetTireOrderActionsQuery,
  useGetTireOrderQuery,
  usePostTireOrderArchiveMutation,
  usePostTireOrderDeleteMutation,
  usePostTireOrderRestoreMutation,
} from '@dms/api/core';
import {useGetCustomerV2Query, useGetUserQuery} from '@dms/api/customer';
import {useGetVehicleQuery} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import {featureFlags} from '@dms/feature-flags';
import {TaskGridPage, useTasksCount} from '@dms/features/tasks';
import i18n from '@dms/i18n';
import {testIds, tiresInventoryRoutes} from '@dms/routes';
import {
  DetailTemplate,
  DetailTemplateHeader,
  EMPTY_PLACEHOLDER,
  EntityHeaderControl,
  EntityHeaderFlag,
  SectionItem,
  TireSetDetail,
  getCustomerName,
  getUserName,
  handleApiError,
  usePermissions,
} from '@dms/shared';

import {buildArray, Nullish, parseDate, useNavigate} from 'shared/web';

import {tireInventorySearchParams} from '../../utils/tireInventorySearchParams';
import {Customer} from './(sections)/Customer/Customer';
import {TireOrderDocuments} from './(sections)/Documents/TireOrderDocuments';
import {SetOfTires} from './(sections)/SetOfTires/SetOfTires';
import {Vehicle} from './(sections)/Vehicle/Vehicle';
import {TireOrderAuthorizationProfileDialog} from './components/TireOrderAuthorizationProfileDialog';
import {useTiresInventoryUrl} from './hooks/useTiresInventoryUrl';

export function TireOrderDetail() {
  const {orderId} = useTiresInventoryUrl();
  const navigate = useNavigate();
  const formatDateTime = useDateTimeFormatter();
  const [isCreateTireSetModalOpen, setIsCreateTireSetModalOpen] = useState(false);
  const [canCreateTireSet] = usePermissions({
    permissionKeys: ['createTireSet'],
  });

  const {
    data: tireOrder,
    isLoading: isTireOrderLoading,
    isError: isTireOrderError,
  } = useGetTireOrderQuery({orderId});
  const {
    data: tireOrderActions,
    isLoading: isTireOrderActionsLoading,
    isError: isTireOrderActionsError,
  } = useGetTireOrderActionsQuery({orderId});
  const {
    data: vehicle,
    isLoading: isVehicleLoading,
    isError: isVehicleError,
  } = useGetVehicleQuery(
    {vehicleId: tireOrder?.vehicleId ?? ''},
    {skip: isNilOrEmpty(tireOrder?.vehicleId)}
  );
  const {
    data: customer,
    isLoading: isCustomerLoading,
    isError: isCustomerError,
  } = useGetCustomerV2Query(
    {customerId: tireOrder?.customerId ?? ''},
    {skip: isNilOrEmpty(tireOrder?.customerId)}
  );
  const {
    data: user,
    isLoading: isUserLoading,
    isError: isUserError,
  } = useGetUserQuery({id: tireOrder?.createdBy ?? ''}, {skip: isNilOrEmpty(tireOrder?.createdBy)});
  const {data: documentCount, isLoading: isDocumentCountLoading} = useGetDocumentContextCountQuery({
    contextTarget: 'tire-order',
    contextId: orderId,
  });

  const [archiveTireOrder] = usePostTireOrderArchiveMutation();
  const [restoreTireOrder] = usePostTireOrderRestoreMutation();
  const [deleteTireOrder] = usePostTireOrderDeleteMutation();

  const [canReadTasks] = usePermissions({
    permissionKeys: ['readTask'],
  });

  const {count: tasksTodoCount} = useTasksCount({
    entityId: orderId,
    relation: EntityResourceIds.tireOrder,
    state: 'TODO',
  });
  const {count: tasksInProgressCount} = useTasksCount({
    entityId: orderId,
    relation: EntityResourceIds.tireOrder,
    state: 'IN_PROGRESS',
  });
  const tasksCountBadge = tasksTodoCount + tasksInProgressCount;
  const isAuthorizationProfileEditable =
    isNilOrEmpty(tireOrder?.vehicleId) && isNotNilOrEmpty(tireOrder?.branchId);

  const handleOpenAuthorizationProfileDialog = () => {
    if (!isAuthorizationProfileEditable) {
      return;
    }

    openDialog(
      <TireOrderAuthorizationProfileDialog
        authorizationProfileId={tireOrder?.authorizationProfileId}
        branchId={tireOrder?.branchId ?? undefined}
        orderId={orderId}
      />,
      {
        title: i18n.t('entity.header.labels.authorizationProfile'),
        size: 'small',
        scrollBehavior: 'outside',
      }
    );
  };

  const handleAction = (key: string) => {
    match(key)
      .with('archive', () => archiveTireOrder({orderId}).unwrap().catch(handleApiError))
      .with('restore', () => restoreTireOrder({orderId}).unwrap().catch(handleApiError))
      .with('delete', () =>
        openDeleteDialog({
          onConfirm: () =>
            deleteTireOrder({orderId})
              .unwrap()
              .then(() => {
                navigate(tiresInventoryRoutes.overview);
              })
              .catch(handleApiError),
        })
      )
      .otherwise(() => catchUnhandledAction(key));
  };

  const menuItems =
    tireOrderActions?.orderActions?.options
      ?.filter((option) => option.style !== 'hidden')
      .map((option, index, list) => ({
        type: option.type,
        label: match(option.key)
          .with('delete', () => i18n.t('general.actions.delete'))
          .with('archive', () => i18n.t('general.actions.archive'))
          .with('restore', () => i18n.t('entity.tireSet.actions.restore'))
          .otherwise(() => i18n.t(`entity.tireOrder.actions.${option.key}`)),
        onClick: () => handleAction(option.key),
        isDisabled: option.style === 'disabled',
        hasSeparator: list[index + 1]?.type === 'divider',
      }))
      .filter((option) => option.type === 'list-item') ?? [];

  const header: DetailTemplateHeader = {
    icon: 'modules/pneu_hotel',
    title: `${i18n.t('page.tiresInventory.labels.tireOrder')} ${
      tireOrder?.number ?? EMPTY_PLACEHOLDER
    }`,
    flags: match<string | Nullish, EntityHeaderFlag[]>(tireOrder?.state)
      .with('OPEN', () => [
        {label: i18n.t('page.tiresInventory.labels.active'), colorScheme: 'green'},
      ])
      .with('ARCHIVED', () => [
        {label: i18n.t('page.tiresInventory.labels.archived'), colorScheme: 'neutral'},
      ])
      .otherwise(() => []),
    parameters: buildArray<Parameter>()
      .when(isNotNilOrEmpty(getCustomerName(customer)), getCustomerName(customer))
      .when(
        isNotNilOrEmpty(vehicle?.vehicle?.registrationPlate),
        vehicle?.vehicle?.registrationPlate
      )
      .when(isNotNilOrEmpty(vehicle?.vin), vehicle?.vin)
      .when(isNotNilOrEmpty(vehicle?.title), vehicle?.title)
      .when(
        isNotNilOrEmpty(getUserName(user)),
        `${i18n.t('general.labels.createdBy')}: ${getUserName(user)}`
      )
      .when(
        isNotNilOrEmpty(tireOrder?.createdAt),
        () =>
          `${i18n.t('general.labels.generated')}: ${formatDateTime(
            'dateShort',
            parseDate(tireOrder?.createdAt ?? '')
          )}`
      ),
    controls: buildArray<EntityHeaderControl>()
      .add('TIRE_BRANCH')
      .when(isNotNilOrEmpty(tireOrder?.authorizationProfileId), 'AUTHORIZATION_PROFILE')
      .add('ASSIGNEE'),
    recordId: orderId,
    resourceId: EntityResourceIds.tireOrder,
    authorizationProfileRecordId: tireOrder?.authorizationProfileId ?? undefined,
    isAuthorizationProfileEditable,
    onAuthorizationProfileChange: handleOpenAuthorizationProfileDialog,
    actions: buildArray<Action>()
      .when(isNotNilOrEmpty(menuItems), {
        type: 'dropdown-iconButton',
        menuItems,
        icon: 'navigation/more_vert',
        priority: 'secondary',
      })
      .add({
        type: 'buttonWithTooltip',
        title: i18n.t('page.tiresInventory.labels.newTireSet'),
        isDisabled: !canCreateTireSet,
        tooltip: {
          label: !canCreateTireSet ? i18n.t('general.labels.noPermission') : undefined,
        },
        onClick: () => setIsCreateTireSetModalOpen(true),
      }),
  };

  const sections = buildArray<SectionItem>([
    {
      id: tireInventorySearchParams.tireOrderDetial.setOfTires,
      label: i18n.t('page.tiresInventory.labels.setOfTires'),
      content: <SetOfTires />,
      'data-testid': testIds.tiresInventory.tireOrderDetail('navigation-setOfTires'),
    },
    {
      id: tireInventorySearchParams.tireOrderDetial.customer,
      label: i18n.t('page.tiresInventory.labels.customer'),
      content: <Customer />,
      hasSeparator: isNilOrEmpty(tireOrder?.vehicleId),
      'data-testid': testIds.tiresInventory.tireOrderDetail('navigation-customer'),
    },
  ])
    .when(isNotNilOrEmpty(tireOrder?.vehicleId), {
      id: tireInventorySearchParams.tireOrderDetial.vehicle,
      label: i18n.t('page.tiresInventory.labels.vehicle'),
      content: <Vehicle />,
      hasSeparator: true,
      'data-testid': testIds.tiresInventory.tireOrderDetail('navigation-vehicle'),
    })
    .add({
      id: tireInventorySearchParams.tireOrderDetial.documents,
      label: i18n.t('page.tiresInventory.labels.documents'),
      content: <TireOrderDocuments />,
      badge: documentCount?.count
        ? {value: documentCount.count, colorScheme: 'neutral'}
        : undefined,
      'data-testid': testIds.tiresInventory.tireOrderDetail('navigation-documents'),
    })
    .when(isFeatureEnabled(featureFlags.CORE_TASK_MANAGEMENT) && canReadTasks, {
      id: 'tasks',
      label: i18n.t('entity.task.labels.tasks'),
      'data-testid': testIds.tiresInventory.tireOrderDetail('navigation-tasks'),
      badge: {
        colorScheme: 'neutral',
        value: tasksCountBadge,
      },
      content: (
        <TaskGridPage
          gridCode="tire-order-task"
          entityId={orderId}
          data-testid={testIds.tiresInventory.tireOrderDetail('tasks')}
        />
      ),
    });

  const isLoading =
    isTireOrderLoading ||
    isVehicleLoading ||
    isCustomerLoading ||
    isUserLoading ||
    isDocumentCountLoading ||
    isTireOrderActionsLoading;
  const isError =
    isTireOrderError || isVehicleError || isCustomerError || isUserError || isTireOrderActionsError;

  return (
    <>
      <Helmet title={i18n.t('page.tiresInventory.labels.tireOrderDetail')} />
      <DetailTemplate
        isLoading={isLoading}
        isError={isError}
        header={header}
        sections={sections}
        data-testid={testIds.tiresInventory.tireOrderDetail('page')}
      />
      {isCreateTireSetModalOpen && (
        <TireSetDetail tireOrderId={orderId} onClose={() => setIsCreateTireSetModalOpen(false)} />
      )}
    </>
  );
}

const catchUnhandledAction = (actionKey: string) => {
  if (process.env.ENV_TYPE === 'dev') {
    return showNotification.error(
      `Tire order - action callback was not specified for action ${actionKey}`
    );
  }

  captureMessage('unhandledTireOrderAction', (scope) =>
    scope.setLevel('error').setExtras({
      userDescription: `Tire order - Action callback was not specified for action ${actionKey}`,
    })
  );
};
